<script setup lang="ts">
import { CircleCheck, Edit } from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { computed, onMounted, ref } from "vue";
import { EmailTemplate, EmailTemplateDto, SysOrgRoleName } from "~/api";
import { apiClient } from "~/utils/apiClient";
import { drawerWidth, emptyGuid } from "~/utils/constants";
import { showError } from "~/utils/handleAPIError";
import FormResponseTemplate from "../pages/FormResponseTemplate.vue";

import { useMyInfoStore } from "~/stores/MyInfoStore";
const myInfoStore = useMyInfoStore();

const emit = defineEmits<{
  (e: "closeDrawer"): void;
  (e: "updateEmailTemplate", emailTemplate: EmailTemplateDto): void;
  (e: "deleted", emailTemplate: EmailTemplateDto): void;
}>();

const props = defineProps<{
  emailTemplateProp: EmailTemplateDto;
}>();
const emailTemplate = ref<EmailTemplate>();
const emailTemplateDto = ref<EmailTemplateDto>();

const drawerOpen = ref(false);

const editing = ref(false);

const hasSetupBasicRole = myInfoStore.currentSysOrg.roles.includes(
  SysOrgRoleName.SETUP_BASIC,
);

// Only users with the setup basic role or the user who created the template
// can edit the template name
const canEditTemplateName = computed(() => {
  return (
    hasSetupBasicRole ||
    emailTemplate.value?.sysOrgGuid === myInfoStore.currentSysOrgGuid
  );
});

const showTemplate = async (templateToShow: EmailTemplateDto) => {
  drawerOpen.value = true;
  if (!templateToShow.guid) {
    showError("Email template not found");
    return;
  }
  await apiClient.emailTemplates
    .getEmailTemplate(templateToShow.guid, myInfoStore.currentSysOrgGuid)
    .then((dto) => {
      emailTemplateDto.value = {
        ...dto,
        updatedAt: dto.updatedAt ?? "",
        workflowDefinitionGuid: dto.workflowDefinitionGuid ?? "",
      };
      emailTemplate.value = dto;
    })
    .catch(showError);
};

const updateEmailTemplate = async () => {
  if (!canEditTemplateName.value) {
    showError("You do not have permission to update email templates");
    return;
  }
  if (!emailTemplate.value?.guid) {
    showError("Email template not found");
    return;
  }

  await apiClient.emailTemplates
    .updateEmailTemplate(emailTemplate.value.guid, emailTemplate.value)
    .then((res) => {
      ElMessage.success("Template updated");
      editing.value = false;

      emit("updateEmailTemplate", res);
    })
    .catch(showError);
};
const handleDeleteEmailTemplate = async (
  emailTemplateGuid: string,
  sysOrgGuid: string,
) => {
  ElMessageBox.confirm(
    "Are you sure you want to delete this email template?",
    "Delete Email Template",
    {
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    },
  )
    .then(async () => {
      if (!emailTemplateGuid) {
        return;
      }
      await apiClient.emailTemplates
        .deleteEmailTemplate(emailTemplateGuid, sysOrgGuid)
        .then((res) => {
          ElMessage.success("Email template deleted");
          emit("closeDrawer");
          emit("deleted", {
            ...res,
            workflowDefinitionGuid: res.workflowDefinitionGuid ?? "",
          });
        })
        .catch(showError);
    })
    .catch(async () => {
      // do nothing
    });
};

const handleClose = async () => {
  emit("closeDrawer");
};

onMounted(async () => {
  await showTemplate(props.emailTemplateProp);
});
</script>

<template>
  <el-drawer
    v-if="emailTemplate?.guid"
    :model-value="drawerOpen"
    :title="emailTemplate.name || '\u00A0'"
    :destroy-on-close="true"
    direction="rtl"
    :size="drawerWidth"
    class="template-drawer"
    @closed="handleClose"
  >
    <template #header>
      <div v-if="editing && canEditTemplateName" class="el-drawer__title">
        <h2>
          <el-input
            v-model="emailTemplate.name"
            placeholder="Email Template Name"
            :style="{ width: '300px' }"
            @keyup.enter="updateEmailTemplate"
          />
        </h2>

        <el-button
          class="el-drawer__close-btn"
          :icon="CircleCheck"
          size="large"
          @click="updateEmailTemplate"
        ></el-button>
      </div>
      <div v-else class="el-drawer__title withEntity">
        <div class="template-info">
          <h2>
            {{ emailTemplate.name }}
          </h2>
          <el-button
            v-if="canEditTemplateName"
            class="el-drawer__close-btn"
            :icon="Edit"
            size="large"
            @click="editing = true"
          ></el-button>
        </div>
      </div>
    </template>

    <FormResponseTemplate
      v-if="emailTemplate.formDefinitionGuid"
      class="form"
      :form-guid="emailTemplate.formDefinitionGuid"
      :response-guid="emailTemplate.formResponseGuid"
    />

    <el-button
      v-if="emailTemplate.guid !== emptyGuid"
      type="danger"
      plain
      @click="
        handleDeleteEmailTemplate(
          emailTemplate.guid,
          myInfoStore.currentSysOrgGuid,
        )
      "
      >Delete Template</el-button
    >
  </el-drawer>
</template>

<style lang="less">
.template-drawer {
  .el-drawer__body {
    overflow-y: auto;
  }
  .el-drawer__header {
    .el-drawer__title {
      display: flex;
      align-items: center;
      .el-input__inner {
        font-size: 1.2rem;
      }

      .template-info {
        display: flex;
        align-items: center;
      }

      &.withEntity {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-right: 11px;
        .entity-info {
          margin-left: 15px;
          cursor: pointer;
          font-size: 1.5rem;
        }
      }
    }
  }
  .form {
    margin-top: 0.5em;
    .viewersBar {
      padding-bottom: 0;
    }
  }
  .archive-btn {
    position: absolute;
    bottom: 1em;
    right: 1em;
    color: #afafaf;
  }

  .template-info {
    display: flex;
    gap: 1em;

    table {
      width: 100%;
      td {
        padding: 0.5em;
      }
    }
  }

  .summary-section {
    display: grid;
    grid-template-columns: auto auto;

    .summary-info {
      display: flex;
      flex-direction: row;
      gap: 0.5em;
      flex-wrap: wrap;
      margin-bottom: 1em;
      .summary-info-row {
        display: flex;
        align-items: center;
        gap: 2em;
        flex-wrap: wrap;
        text-wrap: nowrap;

        .sub-header {
          font-weight: 600;
        }
      }
    }
  }

  .el-collapse-item__header {
    font-size: 1.15em;
    border-radius: 10px;
    padding: 0.3rem 1rem;
    background-color: rgba(229, 229, 229, 0.598);
    box-shadow: inset 0 -1.5px 0 rgba(0, 0, 0, 0.1);
    line-height: normal;
    height: fit-content;
    color: inherit;
    font-weight: inherit;
  }

  .el-collapse-item {
    &.is-active {
      .el-collapse-item__header {
        background-color: var(--el-color-primary-light-7);
      }
    }
  }
}
</style>
